import { useCallback, useEffect, useMemo, useState } from 'react'
import { Banknote, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportZReportHistoricalReport,
  fetchZReportHistoricalReport,
  formatReportDate,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'
import { cn } from '@/lib/utils'

export function ZReportHistoricalReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchZReportHistoricalReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load Z-report history.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportZReportHistoricalReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity = (report?.summary?.shift_count ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Banknote className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Cash / Z-reports</h2>
            <p className="text-sm text-muted-foreground">
              Historical closed-shift Z-reports — sales, voids, payment mix, and cash variance.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Shifts closed" value={String(report.summary.shift_count)} />
            <SummaryCard
              label="Gross sales"
              value={`AED ${formatReportMoney(report.summary.gross_sales)}`}
            />
            <SummaryCard
              label="VAT"
              value={`AED ${formatReportMoney(report.summary.vat_total)}`}
            />
            <SummaryCard
              label="Cash variance"
              value={`AED ${formatReportMoney(report.summary.cash_variance)}`}
            />
          </div>

          <p className="mb-4 text-sm text-muted-foreground">
            Voids: {report.summary.void_count} · AED {formatReportMoney(report.summary.void_amount)}
            {' · '}
            <Link to="/accounts" className="text-primary hover:underline">
              Open Accounts for shift close & print
            </Link>
          </p>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: {report.compare_summary.shift_count} shifts · AED{' '}
              {formatReportMoney(report.compare_summary.gross_sales)} gross
            </p>
          ) : null}

          {report.payment_mix.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Payment mix (period total)
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Method</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {report.payment_mix.map((row) => (
                    <tr key={row.method_code} className="border-t border-border/60">
                      <td className="px-4 py-3">{row.method_name}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.amount)}</td>
                      <td className="px-4 py-3">{row.share_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Report</th>
                  <th className="px-4 py-3 font-medium">Gross</th>
                  <th className="px-4 py-3 font-medium">VAT</th>
                  <th className="px-4 py-3 font-medium">Voids</th>
                  <th className="px-4 py-3 font-medium">Variance</th>
                  <th className="px-4 py-3 font-medium">Counted</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No Z-reports in this period. Close a cash shift on{' '}
                      <Link to="/accounts" className="text-primary hover:underline">
                        Accounts
                      </Link>
                      .
                    </td>
                  </tr>
                ) : (
                  report.reports.map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="px-4 py-3">{formatReportDate(row.business_date)}</td>
                      <td className="px-4 py-3 font-medium">{row.report_number}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.gross_sales)}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.vat_total)}</td>
                      <td className="px-4 py-3">{row.void_count}</td>
                      <td
                        className={cn(
                          'px-4 py-3',
                          Number(row.cash_variance) !== 0 && 'font-medium text-amber-700',
                        )}
                      >
                        AED {formatReportMoney(row.cash_variance)}
                      </td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.closing_cash_counted)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
